import React from 'react';

import EyeIcon from 'assets/images/icons/eye.svg';
import PenIcon from 'assets/images/icons/pen.svg';
import TrashIcon from 'assets/images/icons/trash.svg';
import { ReactComponent as InfoIcon } from 'assets/images/icons/info.svg';

import { Pagination } from 'ui';

import { StatusBadge } from 'modules/Gateways/StatusBadge';

import { Gateway } from 'views/Gateways/Gateways';

import { relativeLastSeenLabel, formatFullDateTime, formatAbsoluteDate } from './lastSeen';

import styles from 'assets/scss/modules/Gateways/Gateways.module.scss';

interface Props {
  items: Gateway[];
  page: number;
  pages: number;
  onPageChange: (page: number) => void;
  onView: (g: Gateway) => void;
  onEdit: (g: Gateway) => void;
  onDelete: (g: Gateway) => void;
}

function renderLastSeen(iso: string | null): React.ReactNode {
  if (!iso) return '-';
  const relative = relativeLastSeenLabel(iso);
  if (relative !== null) {
    return (
      <span className={styles.lastSeenRelative}>
        {relative}
        <span className={styles.lastSeenInfoWrap}>
          <InfoIcon className={styles.lastSeenInfoIcon} />
          <span className={styles.lastSeenTooltip} role="tooltip">
            {formatFullDateTime(iso)}
          </span>
        </span>
      </span>
    );
  }
  const { date, time } = formatAbsoluteDate(iso);
  return (
    <>
      <span className={styles.lastSeenDate}>{date}</span>
      <span className={styles.lastSeenTime}>{time}</span>
    </>
  );
}

export const GatewaysTable: React.FC<Props> = ({
  items,
  page,
  pages,
  onPageChange,
  onView,
  onEdit,
  onDelete,
}) => (
  <>
    <div className={styles.tableWrapper}>
      <div className={styles.tableHeader}>
        <div className={styles.column}>Gateway name</div>
        <div className={styles.column}>Gateway model</div>
        <div className={styles.column}>Freq. band</div>
        <div className={styles.column}>User</div>
        <div className={styles.column}>Object</div>
        <div className={styles.column}>Status</div>
        <div className={styles.column}>Last seen</div>
        <div className={styles.columnActions}>Actions</div>
      </div>

      {items.map((g) => (
        <div key={g.id} className={styles.tableRow}>
          <div className={styles.column} data-label="Gateway name">
            <div className={styles.gatewayName}>{g.name}</div>
            <div className={styles.gatewaySerial}>{g.serial}</div>
          </div>
          <div className={styles.column} data-label="Gateway model">
            <span className={styles.cellText}>{g.model}</span>
          </div>
          <div className={styles.column} data-label="Freq. band">
            <span className={styles.cellText}>{g.freqBand}</span>
          </div>
          <div className={styles.column} data-label="User">
            <span className={styles.cellText}>{g.user ?? '-'}</span>
          </div>
          <div className={styles.column} data-label="Object">
            <span className={styles.cellText}>{g.object ?? '-'}</span>
          </div>
          <div className={styles.column} data-label="Status">
            <StatusBadge status={g.status} />
          </div>
          <div className={styles.column} data-label="Last seen">
            {renderLastSeen(g.lastSeen)}
          </div>
          <div className={styles.columnActions}>
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => onView(g)}
              aria-label="View gateway"
            >
              <img src={EyeIcon} alt="" className={styles.icon} />
            </button>
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => onEdit(g)}
              aria-label="Edit gateway"
            >
              <img src={PenIcon} alt="" className={styles.icon} />
            </button>
            <button
              type="button"
              className={styles.iconButton}
              onClick={() => onDelete(g)}
              aria-label="Delete gateway"
            >
              <img src={TrashIcon} alt="" className={styles.icon} />
            </button>
          </div>
        </div>
      ))}
    </div>

    {pages > 1 && (
      <Pagination pages={pages} currentPage={page} onChange={onPageChange} />
    )}
  </>
);
